import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import '../McmvForm.css';

interface ConsultaInscricaoProps {
  onBackToLogin: () => void;
  onShowHome?: () => void;
}

interface InscricaoData {
  numero_inscricao: string;
  nome_completo: string;
  cpf: string;
  status: string;
  data_cadastro: string;
  data_atualizacao: string;
}

const ConsultaInscricao: React.FC<ConsultaInscricaoProps> = ({ onBackToLogin, onShowHome }) => {
  const [numeroInscricao, setNumeroInscricao] = useState('');
  const [inscricaoData, setInscricaoData] = useState<InscricaoData | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaUrl, setCaptchaUrl] = useState('');

  const refreshCaptcha = () => {
    // Adiciona um timestamp para evitar cache da imagem
    setCaptchaUrl(`${api.defaults.baseURL}/mcmv/captcha?t=${new Date().getTime()}`);
  };

  useEffect(() => {
    refreshCaptcha();
  }, []);

  const [recursos, setRecursos] = useState<Array<{ id: number; texto_recurso: string; resposta: string | null; arquivo_url: string | null; status: string }>>([]);
  const [recursosErro, setRecursosErro] = useState('');

  const loadRecursos = async (numero: string) => {
    try {
      const recResp = await api.get(`/mcmv/recursos/${numero}`);
      setRecursos(recResp.data?.data || []);
      setRecursosErro('');
    } catch (e: any) {
      setRecursosErro(e?.response?.data?.message || 'Erro ao carregar recursos');
      setRecursos([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaInput) {
      setError('Digite o código da imagem.');
      return;
    }
    setIsLoading(true);
    setError('');
    setInscricaoData(null);

    try {
      // 1. Validar o CAPTCHA no backend
      await api.post('/mcmv/captcha/validate', { captcha: captchaInput });

      // 2. Se for válido, consultar a inscrição
      const response = await api.get(`/mcmv/consultar/${numeroInscricao}`);
      setInscricaoData(response.data.data);
      
      // 3. Carregar recursos relacionados
      await loadRecursos(response.data.data.numero_inscricao);

    } catch (error: any) {
      if (error.response?.status === 422) {
        setError('Código da imagem incorreto. Tente novamente.');
        refreshCaptcha(); // Gera um novo CAPTCHA
      } else {
        setError(error.response?.data?.message || 'Erro ao consultar inscrição');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprovado': return '#28a745';
      case 'reprovado': return '#dc3545';
      case 'em_analise': return '#ffc107';
      case 'pendente': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'aprovado': return 'Aprovado';
      case 'reprovado': return 'Reprovado';
      case 'em_analise': return 'Em Análise';
      case 'pendente': return 'Pendente';
      default: return status;
    }
  };

  return (
    <div className="mcmv-form-container">
       <div className="landing-header">
        <div className="header-logos-grid">
          <div className="logo-cell left">
            <img
              src="/images/logo-prefeitura.png"
              alt="Prefeitura de José de Freitas"
              className="header-logo"
            />
          </div>
          <div className="logo-cell center">
            <img
              src="/images/logo-mcmv.png"
              alt="Programa MCMV"
              className="header-logo"
            />
          </div>
          <div className="logo-cell right">
            <img
              src="/images/logo-secretaria.jpg"
              alt="Secretaria Municipal de Assistência Social e Cidadania"
              className="header-logo"
            />
          </div>
        </div>
      </div>
           <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => (onShowHome ? onShowHome() : onBackToLogin())}>Início</button>
          </div>
     <br/>

      <div className="mcmv-form">
        <form onSubmit={handleSubmit} className="form-step">
          <h2>Consultar Inscrição</h2>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Número da Inscrição *</label>
            <input
              type="text"
              value={numeroInscricao}
              onChange={(e) => setNumeroInscricao(e.target.value.toUpperCase())}
              placeholder="Ex: MCMV123400010115"
              required
            />
            <small>Digite o número completo da sua inscrição</small>
          </div>

          {/* CAPTCHA */}
          <div className="form-group">
            <label>Para continuar, digite o texto da imagem</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <img 
                src={captchaUrl} 
                alt="CAPTCHA" 
                style={{ border: '1px solid #ccc', borderRadius: '4px' }} 
              />
              <input
                type="text"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                placeholder="Digite o código"
                style={{ flex: 1, minWidth: '150px' }}
                required
              />
              <button 
                type="button" 
                onClick={refreshCaptcha} 
                className="btn btn-secondary"
                style={{ flexShrink: 0 }}
              >
                &#x21bb;
              </button>
            </div>
          </div>

          <div className="form-navigation">
            <button 
              type="button" 
              onClick={onBackToLogin}
              className="btn btn-secondary"
            >
              Voltar ao Login
            </button>
            <button 
              type="submit" 
              disabled={isLoading} 
              className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}
            >
              {isLoading ? 'Consultando...' : 'Consultar'}
            </button>
          </div>
        </form>

        {inscricaoData && (
          <div className="consulta-resultado" style={{ marginTop: '2rem' }}>
            <h3>Dados da Inscrição</h3>
            <div className="resultado-card">
              <div className="resultado-item">
                <strong>Número:</strong> {inscricaoData.numero_inscricao}
              </div>
              <div className="resultado-item">
                <strong>Nome:</strong> {inscricaoData.nome_completo}
              </div>
              <div className="resultado-item">
                <strong>CPF:</strong> {inscricaoData.cpf}
              </div>
              <div className="resultado-item">
                <strong>Status:</strong> 
                <span 
                  style={{ 
                    color: getStatusColor(inscricaoData.status),
                    fontWeight: 'bold',
                    marginLeft: '0.5rem'
                  }}
                >
                  {getStatusText(inscricaoData.status)}
                </span>
              </div>
              <div className="resultado-item">
                <strong>Data do Cadastro:</strong> {inscricaoData.data_cadastro}
              </div>
              <div className="resultado-item">
                <strong>Última Atualização:</strong> {inscricaoData.data_atualizacao}
              </div>
            </div>

            {/* Formulário de Recurso removido conforme solicitado */}

            {/* Lista de Recursos enviados (sem datas) */}
            <div style={{ marginTop: '2rem' }}>
              <h3>Recursos enviados</h3>
              {recursosErro && <div className="error-message">{recursosErro}</div>}
              {recursos.length === 0 ? (
                <div className="resultado-item">Nenhum recurso enviado.</div>
              ) : (
                <div className="resultado-card">
                  {recursos.map((r) => (
                    <div key={r.id} className="resultado-item">
                      <div><strong>Recurso:</strong> {r.texto_recurso}</div>
                      {r.resposta && (
                        <div><strong>Análise:</strong> {r.resposta}</div>
                      )}
                      <div><strong>Status:</strong> {r.status}</div>
                      {r.arquivo_url && (
                        <div>
                          <strong>Arquivo recebido:</strong> {r.arquivo_url.split('/').pop()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default ConsultaInscricao;